import { Link, Navigate, useSearchParams } from "react-router";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSession } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import { IconAlertOn, IconDown, IconNext, IconSearch, IconX } from "../ui/icons";
import { AsyncButton, Empty, ErrorState, Loading, PageHeader, Switch, useToast } from "../ui/kit";

type Fav = { id: string; title: string; priceCents: number; currency: string; available: boolean; status: string; wasCents: number | null; coverUrl: string | null };
type SavedSearch = { id: string; label: string; query: Record<string, string | number>; alerts: boolean; lastAlertAt: string | null; createdAt: string };

/** Everything a buyer is keeping an eye on: phones they saved (with price drops) and searches that alert them. */
export function Saved() {
  const { t, money, relative } = useI18n();
  const toast = useToast();
  const { me, loading: sLoading } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "searches" ? "searches" : "phones";
  const favs = useAsync(() => (me ? api<{ favorites: Fav[] }>("/api/favorites") : Promise.resolve({ favorites: [] })), [me?.id]);
  const searches = useAsync(() => (me ? api<{ searches: SavedSearch[] }>("/api/saved-searches") : Promise.resolve({ searches: [] })), [me?.id]);
  if (!sLoading && !me) return <Navigate to="/signin?next=/saved" replace />;
  const drops = favs.data?.favorites.filter((f) => f.wasCents && f.available).length ?? 0;
  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <PageHeader title={t("saved.title")} />
      <div className="seg" role="tablist" style={{ marginBottom: "var(--s4)" }}>
        <button role="tab" aria-selected={tab === "phones"} onClick={() => setParams({}, { replace: true })}>
          {t("saved.phones")}{drops ? <span className="badge-dot" style={{ position: "static", marginInlineStart: 6 }}>{drops}</span> : null}
        </button>
        <button role="tab" aria-selected={tab === "searches"} onClick={() => setParams({ tab: "searches" }, { replace: true })}>{t("saved.searches")}</button>
      </div>

      {tab === "phones" ? (
        favs.loading && !favs.data ? <Loading /> : favs.error ? <ErrorState error={favs.error} onRetry={favs.reload} /> : !favs.data!.favorites.length ? (
          <Empty title={t("fav.empty")} body={t("fav.emptyBody")} action={<Link className="btn primary" to="/">{t("welcome.browse")}</Link>} />
        ) : (
          <div className="list">
            {favs.data!.favorites.map((f) => (
              <Link key={f.id} to={`/l/${f.id}`} className="list-item">
                {f.coverUrl ? <img src={f.coverUrl} alt="" /> : <div className="ph" />}
                <div className="grow">
                  <div className="t" dir="auto">{f.title}</div>
                  <div className="s">
                    {f.available ? money(f.priceCents, f.currency) : t(f.status === "reserved" ? "listing.reserved" : "fav.unavailable")}
                    {f.wasCents && f.available ? <> <s className="muted">{money(f.wasCents, f.currency)}</s></> : null}
                  </div>
                  {f.wasCents && f.available ? <span className="tag good"><IconDown size={14} /> {t("saved.dropped", { p: money(f.wasCents - f.priceCents, f.currency) })}</span> : null}
                </div>
                <IconNext size={18} className="muted" />
              </Link>
            ))}
          </div>
        )
      ) : searches.loading && !searches.data ? (
        <Loading />
      ) : searches.error ? (
        <ErrorState error={searches.error} onRetry={searches.reload} />
      ) : !searches.data!.searches.length ? (
        <Empty title={t("saved.noSearches")} body={t("saved.noSearchesBody")} action={<Link className="btn primary" to="/">{t("welcome.browse")}</Link>} />
      ) : (
        <div className="list">
          {searches.data!.searches.map((s) => {
            const qs = new URLSearchParams(Object.entries(s.query).map(([k, v]) => [k, String(v)])).toString();
            return (
              <div key={s.id} className="list-item">
                <IconSearch size={20} className="muted" />
                <Link to={`/?${qs}`} className="grow" style={{ textDecoration: "none", color: "inherit" }}>
                  <div className="t" dir="auto">{s.label}</div>
                  <div className="tiny muted">{s.lastAlertAt ? t("saved.lastAlert", { d: relative(s.lastAlertAt) }) : t("saved.noAlertYet")}</div>
                </Link>
                <span className="row" style={{ gap: 4 }} title={t("saved.alerts")}>
                  <IconAlertOn size={16} className="muted" />
                  <Switch
                    checked={s.alerts}
                    label={t("saved.alerts")}
                    onChange={async (v) => {
                      await api(`/api/saved-searches/${s.id}`, { method: "PATCH", body: { alerts: v } });
                      await searches.reload();
                    }}
                  />
                </span>
                <AsyncButton className="icon-btn" onClick={async () => { await api(`/api/saved-searches/${s.id}`, { method: "DELETE" }); toast(t("saved.deleted")); await searches.reload(); }}>
                  <IconX size={16} /><span className="sr-only">{t("common.remove")}</span>
                </AsyncButton>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
